import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { db, functions } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { useOrgId } from "../utils/tenant";
import useActor from "../utils/useActor";
import { buildResponseIdentity, normalizeResponseIdentity, responseIdentityLabel, responseIdentityStatus } from "../utils/responseIdentity";

export default function StaffTab({ siteScope = { allSites: true, siteIds: [] } }) {
  const orgId = useOrgId();
  const actor = useActor();

  const [rows, setRows] = useState([]); // orgs/{orgId}/cashup_users
  const [stores, setStores] = useState([]); // orgs/{orgId}/cashup_stores
  const [roles, setRoles] = useState([]); // orgs/{orgId}/cashup_roles

  const isArchivedDoc = (d) => d?.isArchived === true || d?.archived === true;

  const activeStores = useMemo(
    () => (stores || []).filter((s) => !isArchivedDoc(s)),
    [stores]
  );
  const activeRoles = useMemo(
    () => (roles || []).filter((r) => !isArchivedDoc(r)),
    [roles]
  );

  // create form
  const [name, setName] = useState("");
  const [pin, setPin] = useState("000000");
  const [roleId, setRoleId] = useState("");
  const [storeId, setStoreId] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [email, setEmail] = useState("");
  const [preferredChannel, setPreferredChannel] = useState("whatsapp");

  // filters/sort
  const [filterStore, setFilterStore] = useState("ALL");
  const [sortBy, setSortBy] = useState("name"); // name | role

  // dialogs
  const [editing, setEditing] = useState(null);
  const [offboarding, setOffboarding] = useState(null);

  // C8.4 — Staff, stores and roles are admin-directory/configuration data.
  // Load them when Staff opens instead of keeping three Firestore listeners alive.
  const loadStaffDirectory = async () => {
    if (!orgId) return;

    const [storesSnap, rolesSnap, usersSnap] = await Promise.all([
      getDocs(collection(db, "orgs", orgId, "cashup_stores")),
      getDocs(collection(db, "orgs", orgId, "cashup_roles")),
      getDocs(collection(db, "orgs", orgId, "cashup_users")),
    ]);

    const allowedSites = new Set(siteScope.siteIds || []);
    setStores(
      storesSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .filter((s) => siteScope.allSites || allowedSites.has(s.id))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    );
    setRoles(
      rolesSnap.docs
        .map((d) => ({ id: d.id, ...(d.data() || {}) }))
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    );
    setRows(usersSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((u) => siteScope.allSites || allowedSites.has(u.storeId)));
  };

  useEffect(() => {
    if (!orgId) return;
    loadStaffDirectory().catch((error) =>
      console.error("[Staff] Failed to load directory", error)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, siteScope.allSites, siteScope.siteIds]);

  // defaults once lists arrive (prefer ACTIVE)
  useEffect(() => {
    if (!roleId) {
      const firstRole = (roles || []).find((r) => !isArchivedDoc(r));
      if (firstRole) setRoleId(firstRole.id);
    }
    if (!storeId) {
      const firstStore = (stores || []).find((s) => !isArchivedDoc(s));
      if (firstStore) setStoreId(firstStore.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, stores]);

  const create = async () => {
    if (!orgId) return;
    if (!name.trim() || !roleId || !storeId) return;

    await addDoc(collection(db, "orgs", orgId, "cashup_users"), {
      orgId,
      name: name.trim(),
      pin: pin || "000000",
      roleId,
      storeId,
      whatsappNumber: whatsappNumber.trim(),
      email: email.trim().toLowerCase(),
      preferredResponseChannel: preferredChannel,
      responseIdentity: buildResponseIdentity({ whatsappNumber, email, preferredChannel }),
      archived: false,
      isArchived: false,
      createdAt: serverTimestamp(),
      createdBy: actor?.name || '',
      createdByUid: actor?.uid || '',
      createdByEmail: actor?.email || '',
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });

    setName("");
    setPin("0000");
    setWhatsappNumber("");
    setEmail("");
    setPreferredChannel("whatsapp");
    await loadStaffDirectory();
  };

  const updateRow = async (id, patch) => {
    await updateDoc(doc(db, "orgs", orgId, "cashup_users", id), {
      ...patch,
      updatedAt: serverTimestamp(),
      updatedBy: actor?.name || '',
      updatedByUid: actor?.uid || '',
      updatedByEmail: actor?.email || '',
    });
    await loadStaffDirectory();
  };

  const sendResponseTest = async (row, channel, { whatsappNumber, email, preferredChannel }) => {
    if (!orgId || !row?.id) throw new Error("Response identity is not available.");

    const responseIdentity = buildResponseIdentity({
      whatsappNumber,
      email,
      preferredChannel,
      previous: row,
    });

    // Save the identity first so the backend always tests the exact number shown in this form.
    await updateRow(row.id, {
      whatsappNumber: String(whatsappNumber || "").trim(),
      email: String(email || "").trim().toLowerCase(),
      preferredResponseChannel: preferredChannel,
      responseIdentity,
    });

    const call = httpsCallable(functions, "sendNeoResponseTest");
    const response = await call({
      orgId,
      personId: row.id,
      personType: "staff",
      channel,
    });
    return response?.data || { ok: false, status: "failed", error: "No response returned." };
  };

  const previewOffboarding = async (row, lastWorkingDate) => {
    const call = httpsCallable(functions, "getNeoStaffOffboardingPreview");
    const response = await call({ orgId, staffId: row.id, lastWorkingDate });
    return response?.data || {};
  };

  const executeOffboarding = async (row, lastWorkingDate, reason) => {
    const call = httpsCallable(functions, "offboardNeoStaff");
    const response = await call({ orgId, staffId: row.id, lastWorkingDate, reason });
    await loadStaffDirectory();
    return response?.data || {};
  };

  const filtered = useMemo(() => {
    let list = [...rows].filter((r) => !(r.isArchived || r.archived));

    // hide legacy rows pointing to missing store/role
    if (stores.length) list = list.filter((r) => stores.some((s) => s.id === r.storeId));
    if (roles.length) list = list.filter((r) => roles.some((x) => x.id === r.roleId));

    if (filterStore !== "ALL") list = list.filter((r) => r.storeId === filterStore);

    list.sort((a, b) => {
      if (sortBy === "role") {
        const ra = roles.find((x) => x.id === a.roleId)?.name || a.roleId || "";
        const rb = roles.find((x) => x.id === b.roleId)?.name || b.roleId || "";
        return ra.localeCompare(rb) || String(a.name || "").localeCompare(String(b.name || ""));
      }
      return String(a.name || "").localeCompare(String(b.name || ""));
    });

    return list;
  }, [rows, filterStore, sortBy, roles, stores]);

  if (!orgId) return <div className="p-6 text-white/70">Loading…</div>;

  return (
    <div className="p-4 md:p-6 text-white space-y-5">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-cyan-300/80">Workforce identity</div>
        <h1 className="mt-1 text-2xl md:text-3xl font-semibold">Staff</h1>
        <p className="mt-1 max-w-2xl text-sm text-white/50">Manage the people Neo can route duties, reminders and future operational responses to.</p>
      </div>

      {/* Create row */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 rounded-2xl border border-white/10 bg-white/[0.03] p-3 md:p-4">
        <input
          className="col-span-1 md:col-span-3 bg-black/40 border border-white/10 rounded px-3 py-2"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <input
          className="col-span-1 md:col-span-2 bg-black/40 border border-white/10 rounded px-3 py-2"
          placeholder="000000"
          type="password"
          inputMode="numeric"
          maxLength={6}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />

        <select
          className="col-span-1 md:col-span-3 bg-black/40 border border-white/10 rounded px-3 py-2"
          value={roleId}
          onChange={(e) => setRoleId(e.target.value)}
        >
          {activeRoles.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>

        <select
          className="col-span-1 md:col-span-3 bg-black/40 border border-white/10 rounded px-3 py-2"
          value={storeId}
          onChange={(e) => setStoreId(e.target.value)}
        >
          {activeStores.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <input
          className="col-span-1 md:col-span-3 bg-black/40 border border-white/10 rounded px-3 py-2"
          placeholder="WhatsApp number"
          value={whatsappNumber}
          onChange={(e) => setWhatsappNumber(e.target.value)}
        />

        <input
          className="col-span-1 md:col-span-4 bg-black/40 border border-white/10 rounded px-3 py-2"
          placeholder="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <select
          className="col-span-1 md:col-span-4 bg-black/40 border border-white/10 rounded px-3 py-2"
          value={preferredChannel}
          onChange={(e) => setPreferredChannel(e.target.value)}
        >
          <option value="whatsapp">WhatsApp preferred</option>
          <option value="email">Email preferred</option>
          <option value="both">WhatsApp + Email</option>
        </select>

        <button
          onClick={create}
          className="col-span-1 md:col-span-1 rounded px-3 py-2 text-white"
          style={{ backgroundColor: "var(--brand-accent)" }}
        >
          Create
        </button>

        {/* Filters */}
        <div className="col-span-1 md:col-span-12 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center mt-2">
          <select
            className="bg-black/40 border border-white/10 rounded px-3 py-2"
            value={filterStore}
            onChange={(e) => setFilterStore(e.target.value)}
          >
            <option value="ALL">All stores</option>
            {activeStores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <select
            className="bg-black/40 border border-white/10 rounded px-3 py-2"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Sort by name</option>
            <option value="role">Sort by role</option>
          </select>
        </div>
      </div>

      {/* Mobile workforce cards */}
      <div className="grid gap-3 md:hidden">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-center text-sm text-white/50">No staff yet.</div>
        ) : (
          filtered.map((r) => {
            const identity = normalizeResponseIdentity(r);
            const ready = responseIdentityStatus(identity) === "ready";
            return (
              <div key={`mobile-${r.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{r.name}</div>
                    <div className="mt-1 text-xs text-white/50">
                      {roles.find((x) => x.id === r.roleId)?.name || r.roleId} · {stores.find((x) => x.id === r.storeId)?.name || r.storeId}
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] ${ready ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                    {responseIdentityLabel(r)}
                  </span>
                </div>
                <div className="mt-4 grid gap-2 text-sm">
                  <div className="rounded-xl bg-black/20 px-3 py-2">
                    <span className="text-white/40">WhatsApp</span>
                    <div className="mt-0.5 break-all">{identity.whatsappNumber || "Not added"}</div>
                  </div>
                  <div className="rounded-xl bg-black/20 px-3 py-2">
                    <span className="text-white/40">Email</span>
                    <div className="mt-0.5 break-all">{identity.email || "Not added"}</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing(r)} className="flex-1 rounded-xl bg-slate-700 px-3 py-2 text-sm">Edit</button>
                  <button onClick={() => setOffboarding(r)} className="rounded-xl bg-rose-600 px-3 py-2 text-sm">Offboard</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Table */}
      <div className="hidden md:block rounded border border-white/10 neo-mobile-table-scroll overflow-x-auto">
        <table className="min-w-[760px] w-full">
          <thead className="bg-white/10">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2">PIN</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Store</th>
              <th className="px-3 py-2">Response</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-3 py-3 text-center text-white/60">
                  No staff yet.
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="border-t border-white/10">
                  <td className="px-3 py-2">{r.name}</td>
                  <td className="px-3 py-2 text-center">{r.pin ? "•".repeat(Math.min(6, String(r.pin).length)) : ""}</td>
                  <td className="px-3 py-2 text-center">
                    {roles.find((x) => x.id === r.roleId)?.name || r.roleId}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {stores.find((x) => x.id === r.storeId)?.name || r.storeId}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {(() => {
                      const identity = normalizeResponseIdentity(r);
                      const ready = responseIdentityStatus(identity) === "ready";
                      return (
                        <div className="inline-flex flex-col items-center gap-1 text-xs">
                          <span className={`rounded-full px-2 py-1 ${ready ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
                            {responseIdentityLabel(r)}
                          </span>
                          <span className="text-white/50">{identity.preferredChannel}</span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setEditing(r)}
                      className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setOffboarding(r)}
                      className="ml-2 px-3 py-1 rounded text-white"
                      style={{ backgroundColor: "#e11d48" }}
                    >
                      Offboard
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditStaffModal
          row={editing}
          roles={activeRoles}
          stores={activeStores}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await updateRow(editing.id, patch);
            setEditing(null);
          }}
          onTest={(channel, identity) => sendResponseTest(editing, channel, identity)}
        />
      )}

      {offboarding && (
        <StaffOffboardingModal
          row={offboarding}
          storeName={stores.find((item) => item.id === offboarding.storeId)?.name || offboarding.storeId || ""}
          onClose={() => setOffboarding(null)}
          onPreview={(lastWorkingDate) => previewOffboarding(offboarding, lastWorkingDate)}
          onConfirm={(lastWorkingDate, reason) => executeOffboarding(offboarding, lastWorkingDate, reason)}
        />
      )}
    </div>
  );
}


function localDateKey(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatLeaveDays(value) {
  const n = Number(value || 0);
  const safe = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
  return `${Number.isInteger(safe) ? safe.toFixed(0) : safe.toFixed(2).replace(/0$/, "")} day${Math.abs(safe) === 1 ? "" : "s"}`;
}

function StaffOffboardingModal({ row, storeName, onClose, onPreview, onConfirm }) {
  const [lastWorkingDate, setLastWorkingDate] = useState(localDateKey());
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingPreview(true);
    setError("");
    setPreview(null);
    onPreview(lastWorkingDate)
      .then((data) => { if (!cancelled) setPreview(data); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Could not prepare offboarding preview."); })
      .finally(() => { if (!cancelled) setLoadingPreview(false); });
    return () => { cancelled = true; };
  }, [lastWorkingDate]);

  const blockedByOpenShift = Number(preview?.openShiftCount || 0) > 0;
  const canConfirm = !!preview && !loadingPreview && !saving && !blockedByOpenShift && reason.trim();

  const submit = async () => {
    if (!canConfirm) return;
    setSaving(true);
    setError("");
    try {
      const result = await onConfirm(lastWorkingDate, reason.trim());
      setDone(result || { ok: true });
    } catch (e) {
      setError(e?.message || "Offboarding failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-3 sm:p-6">
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/10 bg-slate-950 text-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-5 py-4 sm:px-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[.16em] text-cyan-300">Staff offboarding</div>
            <h2 className="mt-1 text-xl font-black">{row.name}</h2>
            <div className="mt-1 text-sm text-white/50">{storeName || "Assigned site"} · preserve history, remove future roster</div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-white/10 px-3 py-2 text-sm text-white/70 hover:bg-white/5">Close</button>
        </div>

        <div className="overflow-y-auto px-5 py-5 sm:px-6">
          {done ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-5">
                <div className="font-black text-emerald-200">Offboarding complete</div>
                <div className="mt-2 text-sm text-emerald-100/80">{row.name} is archived from the active Staff directory. Historical Labor, Payroll and Leave records were preserved.</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <ImpactCard label="Future roster removed" value={`${done.futureRosterEntriesRemoved || 0} entries`} />
                <ImpactCard label="Published shifts removed" value={`${done.futurePublishedShiftsRemoved || 0}`} />
                <ImpactCard label="Leave preserved" value={formatLeaveDays(done.annualLeaveDaysPreserved || 0)} />
                <ImpactCard label="Payroll" value="Final payroll pending" />
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-4 md:grid-cols-[1fr_1.25fr]">
                <div>
                  <label className="text-sm font-semibold">Last working day</label>
                  <input type="date" value={lastWorkingDate} onChange={(e) => setLastWorkingDate(e.target.value)} className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-3" />
                  <p className="mt-2 text-xs leading-5 text-white/45">Neo preserves this date and everything before it. Schedule entries after this day are removed.</p>
                </div>
                <div>
                  <label className="text-sm font-semibold">Reason</label>
                  <textarea rows="3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Resigned · final working day 2 Oct 2026" className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-3 py-3" />
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="font-black">Offboarding impact</div>
                    <div className="mt-1 text-xs text-white/45">Previewed on demand. Nothing changes until you confirm.</div>
                  </div>
                  {loadingPreview && <span className="text-xs font-bold text-cyan-300">Checking…</span>}
                </div>

                {preview && !loadingPreview && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <ImpactCard label="Future roster entries" value={`${preview.futureRosterEntries || 0} removed`} />
                    <ImpactCard label="Published work shifts" value={`${preview.futurePublishedShifts || 0} removed`} />
                    <ImpactCard label="Future leave on roster" value={`${preview.futureLeaveEntries || 0} removed from schedule`} />
                    <ImpactCard label="Annual leave balance" value={preview.leaveBalanceFound ? formatLeaveDays(preview.annualLeaveDays || 0) : "No balance record"} />
                    <ImpactCard label="Labor history" value="Preserved" />
                    <ImpactCard label="Payroll" value="Final payroll pending" />
                  </div>
                )}

                {blockedByOpenShift && (
                  <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-100">
                    {preview.openShiftCount} open shift{preview.openShiftCount === 1 ? "" : "s"} must be resolved in Active Staff/Labor before offboarding. Neo will not guess a clock-out time because that would alter payroll history.
                  </div>
                )}

                {Number(preview?.futureLeaveEntries || 0) > 0 && !blockedByOpenShift && (
                  <div className="mt-4 rounded-xl border border-amber-400/25 bg-amber-400/10 p-3 text-sm text-amber-100">
                    Future leave is removed from the roster only. The leave request, balance and ledger remain unchanged for audit and final-payroll review.
                  </div>
                )}
              </div>

              <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-sm text-white/65">
                <div className="font-bold text-cyan-200">What Neo preserves</div>
                <div className="mt-2 leading-6">Clock-ins and worked hours remain in Labor and Payroll. Leave balances, booked/used leave and the immutable leave ledger remain intact. Payroll is not archived here; archive from Payroll only after the final run.</div>
              </div>

              {error && <div className="mt-4 rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-100">{error}</div>}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-white/10 px-5 py-4 sm:px-6">
          {done ? (
            <button type="button" onClick={onClose} className="rounded-xl bg-cyan-500 px-4 py-2.5 font-bold text-slate-950">Done</button>
          ) : (
            <>
              <button type="button" onClick={onClose} disabled={saving} className="rounded-xl border border-white/10 px-4 py-2.5 text-white/70">Cancel</button>
              <button type="button" onClick={submit} disabled={!canConfirm} className={`rounded-xl px-4 py-2.5 font-bold ${canConfirm ? "bg-rose-600 text-white" : "cursor-not-allowed bg-white/10 text-white/30"}`}>
                {saving ? "Offboarding…" : "Offboard Staff"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ImpactCard({ label, value }) {
  return <div className="rounded-xl border border-white/10 bg-black/20 p-3"><div className="text-[11px] font-bold uppercase tracking-wider text-white/35">{label}</div><div className="mt-1 font-semibold text-white/85">{value}</div></div>;
}


function formatResponseTestTime(value) {
  if (!value) return "—";
  try {
    const d = typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value?.seconds === "number"
        ? new Date(value.seconds * 1000)
        : new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return "—";
  }
}

function EditStaffModal({ row, roles, stores, onClose, onSave, onTest }) {
  const [name, setName] = useState(row.name || "");
  const [pin, setPin] = useState(row.pin || "000000");
  const [roleId, setRoleId] = useState(row.roleId || (roles[0]?.id || ""));
  const [storeId, setStoreId] = useState(row.storeId || (stores[0]?.id || ""));
  const initialIdentity = normalizeResponseIdentity(row);
  const [whatsappNumber, setWhatsappNumber] = useState(initialIdentity.whatsappNumber);
  const [email, setEmail] = useState(initialIdentity.email);
  const [preferredChannel, setPreferredChannel] = useState(initialIdentity.preferredChannel);
  const [testingWhatsapp, setTestingWhatsapp] = useState(false);
  const [whatsappTestResult, setWhatsappTestResult] = useState(initialIdentity.lastWhatsappTest || null);
  const [whatsappTestError, setWhatsappTestError] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(initialIdentity.lastEmailTest || row?.responseIdentity?.lastEmailTest || null);
  const [emailTestError, setEmailTestError] = useState("");

  const runWhatsappTest = async () => {
    setTestingWhatsapp(true);
    setWhatsappTestError("");
    try {
      const result = await onTest("whatsapp", { whatsappNumber, email, preferredChannel });
      setWhatsappTestResult({ ...result, at: new Date().toISOString() });
      if (!result?.ok) setWhatsappTestError([
        result?.error,
        result?.metaDetails,
        result?.template && `Template: ${result.template}`,
        result?.language && `Language: ${result.language}`,
        result?.fbtrace_id && `Trace: ${result.fbtrace_id}`,
      ].filter(Boolean).join("\n") || "WhatsApp test failed.");
    } catch (error) {
      setWhatsappTestError(error?.message || "WhatsApp test failed.");
    } finally {
      setTestingWhatsapp(false);
    }
  };

  const runEmailTest = async () => {
    setTestingEmail(true);
    setEmailTestError("");
    try {
      const result = await onTest("email", { whatsappNumber, email, preferredChannel });
      setEmailTestResult({ ...result, at: new Date().toISOString() });
      if (!result?.ok) setEmailTestError(result?.error || "Email test failed.");
    } catch (error) {
      setEmailTestError(error?.message || "Email test failed.");
    } finally {
      setTestingEmail(false);
    }
  };

  const responseIdentityForSave = () => {
    const persisted = normalizeResponseIdentity(row);

    const currentWhatsapp = String(whatsappNumber || "").trim();
    const currentEmail = String(email || "").trim().toLowerCase();
    const previousWhatsapp = String(persisted.whatsappNumber || "").trim();
    const previousEmail = String(persisted.email || "").trim().toLowerCase();

    const whatsappAddressUnchanged = currentWhatsapp === previousWhatsapp;
    const emailAddressUnchanged = currentEmail === previousEmail;

    const whatsappVerifiedNow =
      whatsappTestResult?.status === "sent" ||
      (whatsappTestResult?.ok === true && whatsappTestResult?.sent === true);

    const emailVerifiedNow =
      emailTestResult?.status === "sent" ||
      (emailTestResult?.ok === true && emailTestResult?.sent === true);

    // Important: save must preserve a successful verification.
    // Only invalidate a channel when its actual address changes.
    return {
      whatsappNumber: currentWhatsapp,
      email: currentEmail,
      preferredChannel,
      whatsappVerified: whatsappAddressUnchanged
        ? (whatsappVerifiedNow || persisted.whatsappVerified === true)
        : whatsappVerifiedNow,
      whatsappVerifiedAt: whatsappAddressUnchanged
        ? (
            whatsappVerifiedNow
              ? (whatsappTestResult?.at || persisted.whatsappVerifiedAt || new Date().toISOString())
              : (persisted.whatsappVerifiedAt || null)
          )
        : (
            whatsappVerifiedNow
              ? (whatsappTestResult?.at || new Date().toISOString())
              : null
          ),
      lastWhatsappTest: whatsappAddressUnchanged
        ? (whatsappTestResult || persisted.lastWhatsappTest || null)
        : (whatsappVerifiedNow ? (whatsappTestResult || null) : null),
      emailVerified: emailAddressUnchanged
        ? (emailVerifiedNow || persisted.emailVerified === true)
        : emailVerifiedNow,
      emailVerifiedAt: emailAddressUnchanged
        ? (
            emailVerifiedNow
              ? (emailTestResult?.at || persisted.emailVerifiedAt || new Date().toISOString())
              : (persisted.emailVerifiedAt || null)
          )
        : (
            emailVerifiedNow
              ? (emailTestResult?.at || new Date().toISOString())
              : null
          ),
      lastEmailTest: emailAddressUnchanged
        ? (emailTestResult || persisted.lastEmailTest || null)
        : (emailVerifiedNow ? (emailTestResult || null) : null),
    };
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end md:items-center justify-center z-50 p-0 md:p-4">
      <div className="bg-[#0f1624] border border-white/10 rounded-t-3xl md:rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-white/10 text-white font-semibold">
          Edit staff
        </div>

        <div className="p-4 text-white">
          <label className="block text-sm mb-1">Name</label>
          <input
            className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2 mb-3"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <label className="block text-sm mb-1">PIN</label>
          <input
            type="password"
            inputMode="numeric"
            maxLength={6}
            autoComplete="new-password"
            className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2 mb-3"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
          />

          <label className="block text-sm mb-1">Role</label>
          <select
            className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2 mb-3"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
          >
            {(roles || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>

          <label className="block text-sm mb-1">Store</label>
          <select
            className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2"
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
          >
            {(stores || []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          <div className="mt-5 rounded-xl border border-cyan-300/15 bg-cyan-300/5 p-4">
            <div className="text-xs uppercase tracking-[0.18em] text-cyan-300">Response identity</div>
            <p className="mt-1 mb-3 text-xs text-white/50">Used by Neo to route future operational WhatsApp and email responses.</p>

            <label className="block text-sm mb-1">WhatsApp number</label>
            <input
              className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2 mb-3"
              placeholder="e.g. +27 82 123 4567"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
            />

            <label className="block text-sm mb-1">Email</label>
            <input
              type="email"
              className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2 mb-3"
              placeholder="name@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <label className="block text-sm mb-1">Preferred response channel</label>
            <select
              className="w-full bg-[#1a2230] border border-white/10 rounded px-3 py-2"
              value={preferredChannel}
              onChange={(e) => setPreferredChannel(e.target.value)}
            >
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
              <option value="both">WhatsApp + Email</option>
            </select>

            <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">WhatsApp readiness test</div>
                  <div className="mt-0.5 text-xs text-white/45">Saves this Response Identity, then sends a tracked MatrixOS transport test.</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] ${
                  whatsappTestResult?.status === "sent" ? "bg-emerald-500/15 text-emerald-300" :
                  whatsappTestResult?.status === "failed" ? "bg-rose-500/15 text-rose-300" :
                  whatsappTestResult?.status === "prepared" ? "bg-amber-500/15 text-amber-300" :
                  "bg-white/10 text-white/50"
                }`}>
                  {whatsappTestResult?.status === "sent" ? "Verified" : whatsappTestResult?.status === "failed" ? "Failed" : whatsappTestResult?.status === "prepared" ? "Prepared" : "Not tested"}
                </span>
              </div>

              {whatsappTestResult?.at && (
                <div className="mt-2 text-xs text-white/45">Last test: {formatResponseTestTime(whatsappTestResult.at)}</div>
              )}
              {whatsappTestResult?.messageId && (
                <div className="mt-1 truncate text-[11px] text-white/35">Message: {whatsappTestResult.messageId}</div>
              )}
              {whatsappTestError && <div className="mt-2 text-xs text-rose-300">{whatsappTestError}</div>}

              <button
                type="button"
                onClick={runWhatsappTest}
                disabled={testingWhatsapp || !String(whatsappNumber || "").trim()}
                className="mt-3 w-full rounded-lg border border-cyan-300/20 bg-cyan-300/10 px-3 py-2 text-sm font-medium text-cyan-200 transition hover:bg-cyan-300/15 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {testingWhatsapp ? "Sending test…" : "Send test WhatsApp"}
              </button>
            </div>

            <div className="mt-3 rounded-xl border border-white/10 bg-black/20 p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">Email readiness test</div>
                  <div className="mt-1 text-xs text-white/45">Saves this Response Identity, then sends a real MatrixOS email test to the address above.</div>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] ${
                  emailTestResult?.status === "sent" ? "bg-emerald-500/15 text-emerald-300" :
                  emailTestResult?.status === "failed" ? "bg-rose-500/15 text-rose-300" :
                  "bg-white/10 text-white/50"
                }`}>
                  {emailTestResult?.status === "sent" ? "Verified" : emailTestResult?.status === "failed" ? "Failed" : "Not tested"}
                </span>
              </div>

              {emailTestResult?.at && (
                <div className="mt-2 text-xs text-white/45">Last test: {formatResponseTestTime(emailTestResult.at)}</div>
              )}
              {emailTestResult?.messageId && (
                <div className="mt-1 truncate text-[11px] text-white/35">Message: {emailTestResult.messageId}</div>
              )}
              {emailTestError && <div className="mt-2 whitespace-pre-line text-xs text-rose-300">{emailTestError}</div>}

              <button
                type="button"
                onClick={runEmailTest}
                disabled={testingEmail || !String(email || "").trim()}
                className="mt-3 w-full rounded-lg border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-sm font-medium text-emerald-200 transition hover:bg-emerald-300/15 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {testingEmail ? "Sending test…" : "Send test Email"}
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600">
            Cancel
          </button>
          <button
            onClick={() => onSave({
              name: name.trim(),
              pin: pin || "000000",
              roleId,
              storeId,
              whatsappNumber: whatsappNumber.trim(),
              email: email.trim().toLowerCase(),
              preferredResponseChannel: preferredChannel,
              responseIdentity: responseIdentityForSave(),
            })}
            className="px-4 py-2 rounded text-white"
            style={{ background: "var(--brand-accent)" }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
